import { useMemo } from 'react';

interface HighlightedResumeProps {
  resumeText: string;
  highlights: string[];
  showHighlights: boolean;
}

interface Segment {
  text: string;
  highlight: boolean;
}

function buildSegments(text: string, phrases: string[]): Segment[] {
  if (!text || phrases.length === 0) return [{ text, highlight: false }];

  // Build a regex from all phrases, sorted longest-first to prefer longer matches
  const escaped = phrases
    .filter(p => p.trim().length > 2)
    .sort((a, b) => b.length - a.length)
    .map(p => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

  if (escaped.length === 0) return [{ text, highlight: false }];

  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(pattern);

  const phraseSet = new Set(phrases.map(p => p.toLowerCase()));
  return parts.map(part => ({
    text: part,
    highlight: phraseSet.has(part.toLowerCase()),
  }));
}

export function HighlightedResume({ resumeText, highlights, showHighlights }: HighlightedResumeProps) {
  const segments = useMemo(
    () => (showHighlights ? buildSegments(resumeText, highlights) : [{ text: resumeText, highlight: false }]),
    [resumeText, highlights, showHighlights]
  );

  return (
    <div className="text-sm text-stone-700 dark:text-stone-200 whitespace-pre-wrap leading-relaxed font-mono text-[13px]">
      {segments.map((seg, i) =>
        seg.highlight ? (
          <mark key={i} className="bg-amber-200 dark:bg-amber-800/60 text-stone-900 dark:text-amber-100 rounded-sm px-0.5">
            {seg.text}
          </mark>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </div>
  );
}
